/**
 * ViolationsPanel
 *
 * Lists all loaded violations grouped by outcome in collapsible sections.
 * Confirmed problems stay expanded; needs-info and excepted rows remain
 * visible as collapsed headers so users see Vera evaluated real rules.
 */
import type { RuleSpec } from "@vera/rules";
import type { Violation } from "@vera/shared";
import { useMemo } from "preact/hooks";
import {
  navigateToSheet,
  useActiveSheetId,
  useActiveSheets,
  useIsUniversalView,
} from "../lib/activeSheets";
import {
  formatUserSuppliedDate,
  latestUserSuppliedAt,
} from "../lib/userFactDraft";
import type { ViolationViewTarget } from "../lib/violationViewTarget";
import {
  resolveViolationViewTarget,
  shouldNavigateToViolation,
} from "../lib/violationViewTarget";
import { useEngineStore } from "../stores/engineStore";
import { useFactPreviewStore } from "../stores/factPreviewStore";
import { useUiModeStore } from "../stores/uiModeStore";
import {
  filterViolationsForDisplay,
  groupViolationsByRule,
  partitionViolationsByOutcome,
} from "../stores/violationFilters";
import { useViolationsStore } from "../stores/violationsStore";
import { Card } from "./Card";

const SEVERITY_RANK: Record<Violation["severity"], number> = {
  error: 0,
  warning: 1,
  info: 2,
};

const SEVERITY_LABELS: Record<Violation["severity"], string> = {
  error: "Error",
  warning: "Warning",
  info: "Info",
};

const SEVERITY_COLORS: Record<Violation["severity"], string> = {
  error: "var(--fc2-violation)",
  warning: "#e5b85f",
  info: "#7fb3d5",
};

function sortBySeverity(violations: Violation[]): Violation[] {
  return [...violations].sort(
    (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
  );
}

function formatUserFactValue(
  value: { value: number; unit: string } | boolean | string
): string {
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    "unit" in value
  ) {
    return `${value.value} ${value.unit}`;
  }
  return String(value);
}

/** Human-readable rule label from the rule pack (falls back to violation text). */
function ruleDisplayInfo(
  rule: RuleSpec | undefined,
  v: Violation
): { name: string; sectionRef: string; sectionTitle?: string } {
  const sectionMatch = v.code.match(/§(.+)$/);
  const sectionRef = sectionMatch ? `§${sectionMatch[1]}` : v.code;
  const name =
    rule?.name.replace(/\.\s*$/, "") ??
    v.summary.split(":")[0]?.trim() ??
    sectionRef;
  return {
    name,
    sectionRef,
    sectionTitle: rule?.source.title,
  };
}

function provenanceBadge(
  v: Violation,
  technical: boolean,
  userVerifiedRow?: boolean
): { label: string; bg: string; fg: string } {
  if (userVerifiedRow || (v.provenance?.userSupplied?.facts.length ?? 0) > 0) {
    return {
      label: "USER VERIFIED",
      bg: "rgba(232,112,112,0.22)",
      fg: "#e87070",
    };
  }
  if (!v.provenance) {
    return { label: "?", bg: "transparent", fg: "var(--fc2-text-dim)" };
  }
  switch (v.provenance.status) {
    case "indeterminate":
      return {
        label: technical ? "INDETERMINATE" : "NEEDS INFO",
        bg: "rgba(229,184,95,0.18)",
        fg: "#e5b85f",
      };
    case "compliant_via_exception":
      return {
        label: "EXCEPTED",
        bg: "rgba(127,179,213,0.18)",
        fg: "#7fb3d5",
      };
    default:
      return {
        label: "VIOLATION",
        bg: "rgba(214,83,83,0.18)",
        fg: "var(--fc2-violation)",
      };
  }
}

export function ViolationsPanel() {
  const violations = useViolationsStore(s => s.violations);
  const selectedId = useViolationsStore(s => s.selectedViolationId);
  const select = useViolationsStore(s => s.selectViolation);
  const loaded = useViolationsStore(s => s.loaded);
  const loadError = useViolationsStore(s => s.loadError);
  const enabledJurisdictions = useEngineStore(s => s.enabledJurisdictions);
  const rules = useEngineStore(s => s.rules);
  const elements = useEngineStore(s => s.elements);
  const activeSheetId = useActiveSheetId();
  const onUniversalView = useIsUniversalView();
  const uiMode = useUiModeStore(s => s.mode);
  const sheets = useActiveSheets();
  const technical = uiMode === "technical";

  const visibleViolations = useMemo(
    () => filterViolationsForDisplay(violations, { enabledJurisdictions }),
    [violations, enabledJurisdictions]
  );

  const groups = useMemo(
    () => partitionViolationsByOutcome(visibleViolations),
    [visibleViolations]
  );

  const rulesById = useMemo(() => {
    const map = new Map<string, RuleSpec>();
    for (const r of rules) map.set(r.ruleId, r);
    return map;
  }, [rules]);

  const onClickViolation = (v: Violation) => {
    const dbId = v.elementDbId;
    const el =
      typeof dbId === "number"
        ? elements.find(e => e.dbId === dbId)
        : undefined;
    const target = resolveViolationViewTarget(v, sheets, activeSheetId, el);
    if (
      target &&
      shouldNavigateToViolation(target, activeSheetId, onUniversalView)
    ) {
      navigateToSheet(target.sheetId);
    }
    select(v.id);
  };

  const metaParts: string[] = [];
  if (groups.confirmed.length)
    metaParts.push(`${groups.confirmed.length} confirmed`);
  if (groups.userVerified.length)
    metaParts.push(`${groups.userVerified.length} verified by you`);
  if (groups.needsInfo.length)
    metaParts.push(`${groups.needsInfo.length} needs info`);
  if (groups.excepted.length)
    metaParts.push(`${groups.excepted.length} excepted`);
  const meta = metaParts.join(" · ") || (loaded ? "0 findings" : undefined);

  const hasAnyFindings =
    groups.confirmed.length +
      groups.userVerified.length +
      groups.needsInfo.length +
      groups.excepted.length >
    0;

  return (
    <Card
      title="Violations"
      defaultOpen
      meta={meta}
      accent="var(--fc2-violation)"
    >
      {!loaded && (
        <div
          style={{
            padding: "8px 4px",
            color: "var(--fc2-text-dim)",
            fontSize: 13,
          }}
        >
          Loading…
        </div>
      )}

      {loadError && (
        <div
          style={{
            padding: "8px 4px",
            color: "var(--fc2-violation)",
            fontSize: 13,
          }}
        >
          Failed to load violations: {loadError}
        </div>
      )}

      {loaded && !loadError && !hasAnyFindings && (
        <div
          style={{
            padding: "8px 4px",
            color: "var(--fc2-text-dim)",
            fontSize: 13,
          }}
        >
          No rule findings for the selected jurisdiction.
        </div>
      )}

      {loaded && !loadError && hasAnyFindings && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <OutcomeSection
            title="Confirmed violations"
            count={groups.confirmed.length}
            defaultOpen
            accent="var(--fc2-violation)"
            emptyMessage="No confirmed problems — expand other sections to see rules FC2 checked."
            violations={groups.confirmed}
            groupByRule={false}
            rulesById={rulesById}
            selectedId={selectedId}
            technical={technical}
            sheets={sheets}
            activeSheetId={activeSheetId}
            elements={elements}
            onSelect={onClickViolation}
          />
          <OutcomeSection
            title="Verified by you"
            count={groups.userVerified.length}
            defaultOpen={groups.userVerified.length > 0}
            accent="#e87070"
            emptyMessage="No violations based on values you supplied."
            violations={groups.userVerified}
            groupByRule
            rulesById={rulesById}
            selectedId={selectedId}
            technical={technical}
            sheets={sheets}
            activeSheetId={activeSheetId}
            elements={elements}
            onSelect={onClickViolation}
            userVerified
          />
          <OutcomeSection
            title={technical ? "Indeterminate" : "Needs info"}
            count={groups.needsInfo.length}
            defaultOpen={
              groups.confirmed.length === 0 && groups.needsInfo.length > 0
            }
            accent="#e5b85f"
            emptyMessage={
              technical
                ? "No indeterminate findings."
                : "No rules waiting on missing BIM data."
            }
            violations={groups.needsInfo}
            groupByRule
            rulesById={rulesById}
            selectedId={selectedId}
            technical={technical}
            sheets={sheets}
            activeSheetId={activeSheetId}
            elements={elements}
            onSelect={onClickViolation}
          />
          <OutcomeSection
            title="Compliant via exception"
            count={groups.excepted.length}
            defaultOpen={false}
            accent="#7fb3d5"
            emptyMessage="No elements matched a code exception."
            violations={groups.excepted}
            groupByRule
            rulesById={rulesById}
            selectedId={selectedId}
            technical={technical}
            sheets={sheets}
            activeSheetId={activeSheetId}
            elements={elements}
            onSelect={onClickViolation}
          />
        </div>
      )}

      {loaded && !loadError && hasAnyFindings && (
        <LabelGlossary technical={technical} />
      )}
    </Card>
  );
}

interface OutcomeSectionProps {
  title: string;
  count: number;
  defaultOpen: boolean;
  accent: string;
  emptyMessage: string;
  violations: Violation[];
  groupByRule: boolean;
  rulesById: Map<string, RuleSpec>;
  selectedId: string | null;
  technical: boolean;
  sheets: ReturnType<typeof useActiveSheets>;
  activeSheetId: string;
  elements: ReturnType<typeof useEngineStore.getState>["elements"];
  onSelect: (v: Violation) => void;
  userVerified?: boolean;
}

function OutcomeSection({
  title,
  count,
  defaultOpen,
  accent,
  emptyMessage,
  violations,
  groupByRule,
  rulesById,
  selectedId,
  technical,
  sheets,
  activeSheetId,
  elements,
  onSelect,
  userVerified = false,
}: OutcomeSectionProps) {
  if (count === 0) {
    return (
      <details
        open={defaultOpen}
        style={{
          border: "1px solid var(--fc2-border)",
          borderRadius: 6,
          padding: "6px 8px",
          borderLeft: `3px solid ${accent}`,
          opacity: 0.75,
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            userSelect: "none",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--fc2-text-dim)",
            letterSpacing: 0.3,
          }}
        >
          {title} <span style={{ fontWeight: 400 }}>(0)</span>
        </summary>
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "var(--fc2-text-dim)",
            paddingLeft: 4,
          }}
        >
          {emptyMessage}
        </div>
      </details>
    );
  }

  const sorted = sortBySeverity(violations);
  const ruleGroups = groupByRule ? groupViolationsByRule(sorted) : null;

  return (
    <details
      open={defaultOpen}
      style={{
        border: "1px solid var(--fc2-border)",
        borderRadius: 6,
        padding: "6px 8px",
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <summary
        style={{
          cursor: "pointer",
          userSelect: "none",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--fc2-text)",
          letterSpacing: 0.3,
        }}
      >
        {title}{" "}
        <span style={{ color: "var(--fc2-text-dim)", fontWeight: 500 }}>
          ({count})
        </span>
      </summary>
      <div style={{ marginTop: 8 }}>
        {ruleGroups
          ? ruleGroups.map(({ ruleId, violations: ruleViolations }) => (
              <RuleGroup
                key={ruleId}
                rule={rulesById.get(ruleId)}
                violations={ruleViolations}
                selectedId={selectedId}
                technical={technical}
                sheets={sheets}
                activeSheetId={activeSheetId}
                elements={elements}
                onSelect={onSelect}
                userVerified={userVerified}
              />
            ))
          : sorted.map(v => (
              <ViolationRow
                key={v.id}
                violation={v}
                rule={rulesById.get(v.provenance?.ruleId ?? "")}
                isSelected={selectedId === v.id}
                technical={technical}
                sheets={sheets}
                activeSheetId={activeSheetId}
                elements={elements}
                onSelect={onSelect}
                userVerified={userVerified}
              />
            ))}
      </div>
    </details>
  );
}

interface RuleGroupProps {
  rule: RuleSpec | undefined;
  violations: Violation[];
  selectedId: string | null;
  technical: boolean;
  sheets: ReturnType<typeof useActiveSheets>;
  activeSheetId: string;
  elements: ReturnType<typeof useEngineStore.getState>["elements"];
  onSelect: (v: Violation) => void;
  userVerified?: boolean;
}

function RuleGroup({
  rule,
  violations,
  selectedId,
  technical,
  sheets,
  activeSheetId,
  elements,
  onSelect,
  userVerified = false,
}: RuleGroupProps) {
  const sample = violations[0];
  if (!sample) return null;
  const display = ruleDisplayInfo(rule, sample);
  const codePrefix = sample.code.replace(/\s*§.+$/, "").trim();

  return (
    <details
      style={{
        marginBottom: 6,
        border: "1px dashed var(--fc2-border)",
        borderRadius: 4,
        padding: "4px 6px",
      }}
    >
      <summary
        style={{
          cursor: "pointer",
          userSelect: "none",
          fontSize: 12,
          fontWeight: 600,
          color: "var(--fc2-text)",
          lineHeight: 1.35,
        }}
      >
        {display.name}{" "}
        <span style={{ color: "var(--fc2-text-dim)", fontWeight: 500 }}>
          ({violations.length})
        </span>
      </summary>
      <div
        style={{
          fontSize: 11,
          color: "var(--fc2-text-dim)",
          margin: "4px 0 6px",
          paddingLeft: 2,
          lineHeight: 1.4,
        }}
      >
        {codePrefix} {display.sectionRef}
        {display.sectionTitle ? ` · ${display.sectionTitle}` : ""}
      </div>
      {sortBySeverity(violations).map(v => (
        <ViolationRow
          key={v.id}
          violation={v}
          rule={rule}
          isSelected={selectedId === v.id}
          technical={technical}
          sheets={sheets}
          activeSheetId={activeSheetId}
          elements={elements}
          onSelect={onSelect}
          compact
          userVerified={userVerified}
        />
      ))}
    </details>
  );
}

interface ViolationRowProps {
  violation: Violation;
  rule: RuleSpec | undefined;
  isSelected: boolean;
  technical: boolean;
  sheets: ReturnType<typeof useActiveSheets>;
  activeSheetId: string;
  elements: ReturnType<typeof useEngineStore.getState>["elements"];
  onSelect: (v: Violation) => void;
  compact?: boolean;
  userVerified?: boolean;
}

function ViolationRow({
  violation: v,
  rule,
  isSelected,
  technical,
  sheets,
  activeSheetId,
  elements,
  onSelect,
  compact = false,
  userVerified = false,
}: ViolationRowProps) {
  const previewId = useFactPreviewStore(s => s.violationId);
  const preview = useFactPreviewStore(s => s.violation);
  const display = isSelected && previewId === v.id && preview ? preview : v;
  const displayRule = rule;
  const el =
    typeof display.elementDbId === "number"
      ? elements.find(e => e.dbId === display.elementDbId)
      : undefined;
  const viewTarget = resolveViolationViewTarget(
    display,
    sheets,
    activeSheetId,
    el
  );
  const badge = provenanceBadge(display, technical, userVerified);
  const ruleInfo = ruleDisplayInfo(displayRule, display);
  const borderColor = userVerified
    ? "#e87070"
    : SEVERITY_COLORS[display.severity];

  return (
    <button
      type="button"
      onClick={() => onSelect(v)}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "10px 12px",
        cursor: "pointer",
        borderRadius: 4,
        background: isSelected ? "var(--fc2-violation-bg)" : "transparent",
        color: "inherit",
        font: "inherit",
        marginBottom: 4,
        fontSize: 13,
        border: "none",
        borderLeft: `3px solid ${borderColor}`,
      }}
    >
      {!compact && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 2,
            }}
          >
            <span
              style={{
                color: "var(--fc2-text)",
                fontWeight: 600,
                lineHeight: 1.35,
              }}
            >
              {ruleInfo.name}
            </span>
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                color: "var(--fc2-text-dim)",
                letterSpacing: 0.5,
                flexShrink: 0,
              }}
            >
              {SEVERITY_LABELS[display.severity]}
            </span>
          </div>
          <div
            style={{
              fontSize: 11,
              color: SEVERITY_COLORS[display.severity],
              marginBottom: 4,
            }}
          >
            {ruleInfo.sectionRef}
            {ruleInfo.sectionTitle ? ` · ${ruleInfo.sectionTitle}` : ""}
          </div>
        </>
      )}
      <div style={{ marginBottom: compact ? 2 : 4 }}>{display.summary}</div>
      {userVerified && display.provenance?.userSupplied?.facts.length ? (
        <div
          style={{
            fontSize: 11,
            color: "#e87070",
            marginBottom: compact ? 2 : 4,
            lineHeight: 1.4,
          }}
        >
          You provided{" "}
          {formatUserSuppliedDate(
            latestUserSuppliedAt(display.provenance!.userSupplied!.facts) ?? ""
          )}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: "var(--fc2-text-dim)",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            background: badge.bg,
            color: badge.fg,
            padding: "1px 6px",
            borderRadius: 2,
            fontSize: 9,
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {badge.label}
        </span>
        {v.provenance?.source === "engine" &&
          v.provenance.cite?.jurisdictionId && (
            <span
              style={{
                background: "rgba(127,179,213,0.18)",
                color: "#7fb3d5",
                padding: "1px 6px",
                borderRadius: 2,
                fontSize: 9,
                letterSpacing: 0.5,
                fontWeight: 600,
              }}
              title={v.provenance.cite.jurisdictionDisplay}
            >
              {v.provenance.cite.jurisdictionId.toUpperCase()}
            </span>
          )}
        {technical &&
          v.provenance?.confidence &&
          v.provenance.confidence.tier !== "high" && (
            <span
              style={{
                background: "rgba(229,184,95,0.18)",
                color: "#e5b85f",
                padding: "1px 6px",
                borderRadius: 2,
                fontSize: 9,
                letterSpacing: 0.5,
                fontWeight: 600,
              }}
              title={`Family-1 validator: ${v.provenance.confidence.tier} — ${v.provenance.confidence.findings.length} finding(s). Click the violation to see them in the source panel.`}
            >
              REVIEW
            </span>
          )}
        {v.provenance?.userSupplied?.facts.map(f => (
          <span
            key={f.factKey}
            style={{
              fontSize: 10,
              color: "var(--fc2-text-dim)",
            }}
            title={`First supplied ${formatUserSuppliedDate(f.suppliedAt)}`}
          >
            {f.label ?? f.factKey}: {formatUserFactValue(f.value)} ·{" "}
            {formatUserSuppliedDate(f.updatedAt)}
          </span>
        ))}
        {viewTarget && (
          <ViolationSheetBadge target={viewTarget} technical={technical} />
        )}
        {technical && <span>dbId {v.elementDbId ?? "—"}</span>}
      </div>
    </button>
  );
}

function ViolationSheetBadge({
  target,
  technical,
}: {
  target: ViolationViewTarget;
  technical: boolean;
}) {
  const onSheet = !target.autoSwitch;
  const bg =
    target.kind === "3d" ? "rgba(110,86,207,0.18)" : "rgba(127,179,213,0.15)";
  const fg = target.kind === "3d" ? "#a78bfa" : "#7fb3d5";

  return (
    <span
      title={target.hint}
      style={{
        background: bg,
        color: fg,
        padding: "1px 6px",
        borderRadius: 2,
        fontSize: 9,
        letterSpacing: 0.4,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {onSheet ? "On this sheet" : target.badge}
      {technical && onSheet ? ` · ${target.sheetName}` : ""}
    </span>
  );
}

function LabelGlossary({ technical }: { technical: boolean }) {
  return (
    <details
      style={{
        marginTop: 12,
        paddingTop: 8,
        borderTop: "1px dashed var(--fc2-border)",
        fontSize: 11,
        color: "var(--fc2-text-dim)",
        lineHeight: 1.5,
      }}
    >
      <summary
        style={{
          cursor: "pointer",
          userSelect: "none",
          color: "var(--fc2-text-dim)",
          fontWeight: 600,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          fontSize: 10,
        }}
      >
        What do these labels mean?
      </summary>
      <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
        <li>
          <strong style={{ color: "var(--fc2-violation)" }}>VIOLATION</strong>{" "}
          (red) — FC2 measured the element against the rule and the measurement
          does NOT comply. Confirmed problem.
        </li>
        <li>
          <strong style={{ color: "#e87070" }}>USER VERIFIED</strong> (coral) —
          you supplied the missing measurement; FC2 re-checked with your value.
          Version and timestamp are stored in the local audit log.
        </li>
        <li>
          <strong style={{ color: "#e5b85f" }}>
            {technical ? "INDETERMINATE" : "NEEDS INFO"}
          </strong>{" "}
          (amber) — FC2 couldn't decide because a fact the rule needs is missing
          from the BIM data (e.g. window net-clear opening area, stair
          headroom). Flag for human review; FC2 doesn't guess.
        </li>
        <li>
          <strong style={{ color: "#7fb3d5" }}>EXCEPTED</strong> (blue) — FC2
          matched a code exception that makes the element compliant despite
          missing the base requirement.
        </li>
        {technical && (
          <li>
            <strong style={{ color: "#e5b85f" }}>REVIEW</strong> — the rule
            itself was flagged by the Family-1 validator (ADR 0007) as not
            high-confidence at extraction time. The verdict needs a human
            spot-check on top of FC2's measurement.
          </li>
        )}
      </ul>
    </details>
  );
}
